﻿import { invalidate } from '@react-three/fiber';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Vector3 } from 'three';

import { isPlacementIndicator } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import {
    acquirePanelDimensioningWorker,
    releasePanelDimensioningWorker,
    WorkerDimensionPanel
} from 'app/modules/wallforming/components/workers/panelDimensioningWorkerSingleton';
import { EPSILON } from 'shared/constants';

import type { Point3D } from 'api/models';
import type { DimensionPanel } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionPanel';
import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';

interface CreatePanelDimensioningProps {
    articleColliders: ColliderPoint[];
    selectedPoiIndex: number | undefined;
    mid: Vector3;
    planeNormal: Vector3 | undefined;
    floorZ: number;
    groupedInstanceIds: Set<string>;
    instanceToGroupMap: Record<string, string>;
}

interface DimensioningResult {
    panels: DimensionPanel[];
}

const TIMEOUT_DELAY_MS = 100;
const defaultResult: DimensioningResult = { panels: [] };

export const useCreatePanelDimensioning = ({
    articleColliders,
    selectedPoiIndex,
    mid,
    planeNormal,
    floorZ,
    groupedInstanceIds,
    instanceToGroupMap
}: CreatePanelDimensioningProps): DimensioningResult => {
    const [result, setResult] = useState<DimensioningResult>(defaultResult);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const workerRef = useRef<Worker | undefined>(undefined);
    const generationRef = useRef<number>(0);
    const eventListenerRef = useRef<((e: MessageEvent) => void) | null>(null);

    useEffect(() => {
        workerRef.current = acquirePanelDimensioningWorker();

        return () => {
            clearTimeout(debounceTimerRef.current);
            if (workerRef.current && eventListenerRef.current) {
                workerRef.current.removeEventListener('message', eventListenerRef.current);
                eventListenerRef.current = null;
            }
            releasePanelDimensioningWorker();
            workerRef.current = undefined;
        };
    }, []);

    const computePanelDimensions = useCallback(() => {
        const state = usePlacementStore.getState();
        if (!state.isAutoDragActiveRef.current && state.isDraggingActive()) {
            return;
        }

        if (!workerRef.current || !articleColliders || selectedPoiIndex === undefined || !planeNormal) {
            setResult(defaultResult);
            invalidate();
            return;
        }

        const currentGeneration = ++generationRef.current;

        const wallNormal = planeNormal.clone().normalize();
        const bestPartScoreByRelation = new Map<string, Map<string, number>>();
        articleColliders.forEach((collider) => {
            if (!collider.name || !collider.alignment) {
                return;
            }

            const score = Math.abs(collider.alignment.clone().normalize().dot(wallNormal));
            const scoresByPart = bestPartScoreByRelation.get(collider.relationId) ?? new Map<string, number>();
            scoresByPart.set(collider.name, Math.max(scoresByPart.get(collider.name) ?? 0, score));
            bestPartScoreByRelation.set(collider.relationId, scoresByPart);
        });

        // Skip temporary placement indicators and retain the instance part facing this wall.
        const colliders = articleColliders
            .filter((c) => !isPlacementIndicator(c.relationId, state))
            .filter((c) => {
                if (!c.name || !c.alignment) {
                    return true;
                }
                const scoresByPart = bestPartScoreByRelation.get(c.relationId);
                const bestScore = scoresByPart ? Math.max(...scoresByPart.values()) : 0;
                return (scoresByPart?.get(c.name) ?? 0) >= bestScore - EPSILON;
            })
            .map((c) => ({
                poiGuids: c.poiGuids,
                position: {
                    x: c.position.x,
                    y: c.position.y,
                    z: c.position.z
                },
                relationId: c.relationId
            }));

        const worker = workerRef.current;

        if (eventListenerRef.current) {
            worker.removeEventListener('message', eventListenerRef.current);
        }

        const handleWorkerMessage = (e: MessageEvent) => {
            worker.removeEventListener('message', handleWorkerMessage);
            eventListenerRef.current = null;

            if (currentGeneration !== generationRef.current) {
                return;
            }

            const data = e.data as { panels: WorkerDimensionPanel[] };

            // Compute panel-face orientation from plane normal
            const normal = planeNormal.clone().normalize();
            const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y);

            const u: Point3D = {
                x: length > EPSILON ? -normal.y / length : 1,
                y: length > EPSILON ? normal.x / length : 0,
                z: 0
            };

            const v: Point3D = {
                x: normal.y * u.z - normal.z * u.y,
                y: normal.z * u.x - normal.x * u.z,
                z: normal.x * u.y - normal.y * u.x
            };

            const panels: DimensionPanel[] = data.panels.map((wp) => ({
                ...wp,
                normal,
                u,
                v,
                center: {
                    x: wp.center.x,
                    y: wp.center.y,
                    z: wp.center.z
                }
            }));

            setResult({ panels });
            invalidate();
        };

        const selectedPoiGuid = state.pois[selectedPoiIndex]?.poi.poiGuid;
        if (!selectedPoiGuid) {
            setResult(defaultResult);
            invalidate();
            return;
        }

        eventListenerRef.current = handleWorkerMessage;
        worker.addEventListener('message', handleWorkerMessage);
        worker.postMessage({
            colliders,
            floorZ,
            mid: { x: mid.x, y: mid.y, z: mid.z },
            planeNormal: { x: planeNormal.x, y: planeNormal.y, z: planeNormal.z },
            selectedPoiIndex,
            groupedRelationIds: [...groupedInstanceIds],
            groupIdByRelationId: instanceToGroupMap,
            poiGuids: new Set([selectedPoiGuid])
        });
    }, [
        articleColliders,
        selectedPoiIndex,
        planeNormal,
        floorZ,
        mid.x,
        mid.y,
        mid.z,
        groupedInstanceIds,
        instanceToGroupMap
    ]);

    useEffect(() => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(computePanelDimensions, TIMEOUT_DELAY_MS);
    }, [computePanelDimensions]);

    return result;
};
